import { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import {
  BASIC_DARK_GRAY,
  BASIC_GRAY_BACKGROUND,
  BASIC_LINE,
} from "../../../constants/colors";

type Props = {
  initialCount: number;
  onChange: (count: number) => void;
  size?: "sm" | "md";
  disabled?: boolean;
  limited?: boolean;
  limitedDisabled?: boolean;
  shouldReset?: boolean;
};

export const MenuCountSelect: React.FC<Props> = ({
  initialCount,
  onChange,
  size = "md",
  disabled,
  limited,
  limitedDisabled,
  shouldReset,
}) => {
  const [count, setCount] = useState<number>(initialCount);

  useEffect(() => {
    if (shouldReset) setCount(initialCount);
  }, [shouldReset]);

  const handleSetCount = useCallback(
    (add: boolean) => {
      if (disabled || limited) return;
      if (add && count === 99) return;
      if (!add && count === 0) return;

      if (add) {
        onChange(count + 1);
        setCount(count + 1);
      } else {
        onChange(count - 1);
        setCount(count - 1);
      }
    },
    [count, onChange, limitedDisabled, disabled],
  );

  return (
    <MenuCountSelectContainer disabled={(disabled ?? false) && !limited}>
      <MenuCountButton
        size={size}
        disabled={(disabled ?? false) || (limited ?? false)}
        onClick={() => handleSetCount(false)}
      >
        -
      </MenuCountButton>
      <MenuCounter
        size="sm"
        type="text"
        readOnly
        value={count}
        disabled={disabled}
      />
      <MenuCountButton
        disabled={(disabled ?? false) || (limited ?? false) || count === 99}
        size={size}
        onClick={() => handleSetCount(true)}
      >
        +
      </MenuCountButton>
    </MenuCountSelectContainer>
  );
};

const MenuCountSelectContainer = styled.div<{ disabled: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  opacity: ${(props) => (props.disabled ? 0.25 : 1)};
`;

const MenuCountButton = styled.div<{ disabled: boolean; size: "sm" | "md" }>`
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.disabled ? 0.25 : 1)};
  width: ${(props) => (props.size === "sm" ? "30px" : "50px")};
  text-align: center;
  padding: 5px 0;
  margin: 0;
  background-color: ${BASIC_GRAY_BACKGROUND};
  font-size: 14px;
  box-sizing: border-box;
  color: ${BASIC_DARK_GRAY};
  font-weight: normal;
  border: 1px solid ${BASIC_DARK_GRAY};
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  border-left: none;
  border-right: none;
  user-select: none;
  &:hover {
    background-color: ${BASIC_LINE};
  }

  &:first-of-type {
    border-left: 1px solid ${BASIC_DARK_GRAY};
  }

  &:last-of-type {
    border-right: 1px solid ${BASIC_DARK_GRAY};
  }
`;

const MenuCounter = styled.input<{ size: "sm" | "md" }>`
  border-radius: 0;
  outline: none;
  font-size: 14px;
  font-weight: 600;
  width: ${(props) => (props.size === "sm" ? "30px" : "50px")};
  text-align: center;
  padding: 5px 0;
  margin: 0;
  border-left: none;
  border-right: none;
  border: 1px solid ${BASIC_DARK_GRAY};
  &:disabled {
    cursor: not-allowed;
  }
`;
